"use client";

import { createContext, type FC, useContext, useEffect, useState } from "react";

import { type google, type LoaderOptions, safeLoadGoogleMapsApi } from "./utils/safeLoadGoogleMapsApi";

export type { google };

export const GoogleAPIContext = createContext<google | null>(null);

export type GoogleAPIProviderProps = {
  apiKey: string;
  options?: LoaderOptions;
  children: React.ReactNode;
};

export const GoogleAPIProvider: FC<GoogleAPIProviderProps> = ({ apiKey, options, children }) => {
  const [googleObj, setGoogleObj] = useState<google | null>(null);

  useEffect(() => {
    const loadGoogleMaps = async () => {
      if (!apiKey || googleObj) {
        return;
      }

      await safeLoadGoogleMapsApi(apiKey, options)
        .then((loadedGoogle) => {
          setGoogleObj(loadedGoogle);
        })
        .catch((error) => {
          console.error("Failed to load Google Maps API", error);
        });
    };

    loadGoogleMaps();
  }, [apiKey, googleObj, options]);

  return <GoogleAPIContext.Provider value={googleObj}>{children}</GoogleAPIContext.Provider>;
};

export function useGoogleAPI(): google | null {
  const context = useContext(GoogleAPIContext);

  if (context === undefined) {
    console.error("useGoogleAPI must be used within a GoogleAPIProvider");
    return null;
  }

  return context;
}
